import { ReactNode, useMemo, useRef, useState } from 'react';
import { Button } from '@tigergraph/app-ui-lib/button';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Plus } from 'baseui/icon';
import { PLACEMENT, Popover } from 'baseui/popover';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import toast from 'react-hot-toast';
import { expand } from 'inline-style-expand-shorthand';

import EditableText, { EditableTextHandle } from '@/components/EditableText';
import { popOverrides } from '@/components/popOverride';
import { showToast } from '@/components/styledToasterContainer';
import { useOrgContext } from '@/contexts/orgContext';
import { parseDate } from '@/lib/date';
import { calculateRoleForGroup, calculateRoleForSpace } from '@/pages/admin/user/type';
import {
  useMutationDeleteDatabase,
  useMutationRenameDatabase,
  useMutationUpdateWorkspace,
} from '@/pages/workgroup/hook';
import {
  ConfirmDeleteDatabase,
  ConfirmResumeWorkspace,
  SettingMenuForWorkSpace,
} from '@/pages/workgroup/tab/SettingMenu';
import { StatusIndicator, WorkspacePopoverContent } from '@/pages/workgroup/tab/General';
import { DatabasePopoverContent } from '@/pages/workgroup/tab/general/DatabaseNamePopover';
import { DatabaseT, WorkGroupT, WorkspaceT, isStatusActive } from '@/pages/workgroup/type';
import { onboardingAtom, defaultOnboardingState } from '@/pages/onboarding/state';
import { getErrorMessage } from '@/utils/utils';

import { SnapshotTag } from './SnapshotTag';
import { formatConfigurationLabel, orderWorkspaceRowsForCardView } from './utils';
import { WorkspaceRow } from './types';

export type CardViewProps = {
  group: WorkGroupT;
  rows: WorkspaceRow[];
  orphanDatabases: DatabaseT[];
  selectedIds: Set<string>;
  selectedDatabaseIds: Set<string>;
  onToggleRow: (workspace: WorkspaceT) => void;
  onToggleDatabase: (database: DatabaseT) => void;
  isCreatingDisabled: boolean;
  canCreate: boolean;
  onCreate: () => void;
  emptyMessage?: string;
};

export default function CardView({
  group,
  rows,
  orphanDatabases,
  selectedIds,
  selectedDatabaseIds,
  onToggleRow,
  onToggleDatabase,
  isCreatingDisabled,
  canCreate,
  onCreate,
  emptyMessage,
}: CardViewProps) {
  const [css, theme] = useStyletron();

  const databaseById = useMemo(
    () =>
      group.tg_databases.reduce<Record<string, DatabaseT>>((acc, database) => {
        acc[database.database_id] = database;
        return acc;
      }, {}),
    [group.tg_databases]
  );

  const orderedRows = useMemo(() => orderWorkspaceRowsForCardView(rows), [rows]);

  if (orderedRows.length === 0 && orphanDatabases.length === 0) {
    return (
      <div
        className={css({
          padding: '32px 16px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '12px',
          color: theme.colors['text.secondary'],
          backgroundColor: theme.colors['background.alternative'],
          borderRadius: '12px',
          border: `1px solid ${theme.colors.divider}`,
        })}
      >
        <span>{emptyMessage || 'No workspaces match your filters.'}</span>
        {canCreate ? (
          <Button
            kind="secondary"
            size="compact"
            disabled={isCreatingDisabled}
            onClick={onCreate}
            startEnhancer={<Plus size={16} />}
          >
            Create Workspace
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={css({
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
        alignItems: 'start',
        gap: '16px',
      })}
    >
      {orderedRows.map((row) => {
        const database = row.workspace.database_id ? databaseById[row.workspace.database_id] : undefined;
        return (
          <WorkspaceCard
            key={row.workspace.workspace_id}
            group={group}
            workspace={row.workspace}
            database={database}
            databaseName={database?.name || row.databaseName}
            selectedIds={selectedIds}
            onToggleRow={onToggleRow}
          />
        );
      })}
      {orphanDatabases.map((database) => (
        <StandaloneDatabaseCard
          key={database.database_id}
          database={database}
          checked={selectedDatabaseIds.has(database.database_id)}
          onToggle={() => onToggleDatabase(database)}
        />
      ))}
    </div>
  );
}

function WorkspaceCard({
  group,
  workspace,
  database,
  databaseName,
  selectedIds,
  onToggleRow,
}: {
  group: WorkGroupT;
  workspace: WorkspaceT;
  database: DatabaseT | undefined;
  databaseName: string;
  selectedIds: Set<string>;
  onToggleRow: (workspace: WorkspaceT) => void;
}) {
  const [css, theme] = useStyletron();
  const checked = selectedIds.has(workspace.workspace_id);

  const relatedRwWorkspace = useMemo(() => {
    if (workspace.is_rw || !workspace.database_id) {
      return undefined;
    }
    return group.workspaces.find((w) => w.is_rw && w.database_id === workspace.database_id);
  }, [group.workspaces, workspace.database_id, workspace.is_rw]);

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        padding: '16px',
        borderRadius: '8px',
        border: `1px solid ${checked ? theme.colors['border.selected'] : theme.colors.divider}`,
        backgroundColor: theme.colors['background.alternative'],
      })}
    >
      <WorkspaceCardBody
        group={group}
        workspace={workspace}
        database={database}
        databaseName={databaseName}
        relatedRwWorkspace={relatedRwWorkspace}
        checked={checked}
        onToggle={() => onToggleRow(workspace)}
      />
    </div>
  );
}

function WorkspaceCardBody({
  group,
  workspace,
  database,
  databaseName,
  relatedRwWorkspace,
  checked,
  onToggle,
}: {
  group: WorkGroupT;
  workspace: WorkspaceT;
  database: DatabaseT | undefined;
  databaseName?: string;
  relatedRwWorkspace?: WorkspaceT;
  checked?: boolean;
  onToggle?: () => void;
}) {
  const [css] = useStyletron();

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      })}
    >
      <CardHeader group={group} workspace={workspace} database={database} checked={checked} onToggle={onToggle} />
      <StatusBadge workspace={workspace} />

      <CardDivider />

      <DetailRow
        label="Database:"
        value={
          <DatabaseValue
            name={databaseName ?? ''}
            database={database}
            workspace={workspace}
            relatedRwWorkspace={relatedRwWorkspace}
          />
        }
      />
      <DetailRow label="Access:" value={<AccessBadge isRw={workspace.is_rw} />} />
      <DetailRow label="Configuration:" value={<span>{formatConfigurationLabel(workspace)}</span>} />

      <CardFooter workspace={workspace} />
    </div>
  );
}

function CardHeader({
  group,
  workspace,
  database,
  checked,
  onToggle,
}: {
  group: WorkGroupT;
  workspace: WorkspaceT;
  database: DatabaseT | undefined;
  checked?: boolean;
  onToggle?: () => void;
}) {
  const [css] = useStyletron();
  const { userInfo } = useOrgContext();
  const updateName = useMutationUpdateWorkspace();
  const handleRef = useRef<EditableTextHandle | null>(null);
  const [showPopover, setShowPopover] = useState(false);
  const [showResume, setShowResume] = useState(false);

  const effectRole = calculateRoleForSpace(userInfo.roles, workspace.workgroup_id, workspace.workspace_id);
  const canEdit = effectRole === 'workspace-admins';

  const delayClose = () => {
    setTimeout(() => setShowPopover(false), 100);
  };

  return (
    <div
      className={css({
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      })}
    >
      <div
        className={css({
          flex: 1,
          minWidth: 0,
          fontSize: '16px',
          fontWeight: 600,
          color: '#156BBF',
          position: 'relative',
        })}
      >
        <EditableText
          ref={handleRef}
          text={workspace.name}
          compact
          maxWidth="100%"
          disableEdit={!canEdit}
          onClick={() => setShowPopover((prev) => !prev)}
          onTextChange={(name) => {
            updateName.mutate(
              {
                group_id: workspace.workgroup_id,
                space_id: workspace.workspace_id,
                data: {
                  workspace_name: name,
                },
              },
              {
                onError(error) {
                  showToast({
                    kind: 'negative',
                    message: `${getErrorMessage(error)}`,
                  });
                  handleRef.current?.reset();
                },
              }
            );
          }}
        />
        <Popover
          isOpen={showPopover}
          onEsc={delayClose}
          onClickOutside={() => {}}
          content={
            <WorkspacePopoverContent
              workspace={workspace}
              database={database}
              onClose={() => setShowPopover(false)}
              onResume={() => setShowResume(true)}
              group={group}
            />
          }
          placement={PLACEMENT.bottom}
          overrides={popOverrides}
          autoFocus={false}
        >
          <div
            className={css({
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
            })}
          />
        </Popover>
      </div>
      {onToggle ? (
        <Checkbox
          aria-label={`Select workspace ${workspace.name}`}
          checked={checked}
          onChange={onToggle}
          overrides={{
            Root: {
              style: {
                marginRight: 0,
              },
            },
          }}
        />
      ) : null}
      <SettingMenuForWorkSpace workspace={workspace} database={database} borderless showNavItems />
      {showResume ? (
        <ConfirmResumeWorkspace isOpen={showResume} onClose={() => setShowResume(false)} workspace={workspace} />
      ) : null}
    </div>
  );
}

function StatusBadge({ workspace }: { workspace: WorkspaceT }) {
  const [css, theme] = useStyletron();
  return (
    <div
      className={css({
        display: 'inline-flex',
        alignSelf: 'flex-start',
        alignItems: 'center',
        gap: '6px',
        padding: '4px 10px',
        backgroundColor: theme.colors['tag.background.default'],
        borderRadius: '14px',
        fontSize: '12px',
      })}
    >
      <StatusIndicator status={workspace.status} errMessage={workspace.err_message} size="compact" />
      <span>{workspace.status}</span>
    </div>
  );
}

function DatabaseValue({
  name,
  database,
  workspace,
  relatedRwWorkspace,
}: {
  name: string;
  database: DatabaseT | undefined;
  workspace: WorkspaceT;
  relatedRwWorkspace?: WorkspaceT;
}) {
  const [css, theme] = useStyletron();
  const { userInfo } = useOrgContext();
  const updateName = useMutationRenameDatabase();
  const handleRef = useRef<EditableTextHandle | null>(null);
  const [showPopover, setShowPopover] = useState(false);

  const effectRole = calculateRoleForSpace(userInfo.roles, workspace.workgroup_id, workspace.workspace_id);
  // Database rename is owned by the R/W workspace card (same as Graph view).
  const canEdit = workspace.is_rw && effectRole === 'workspace-admins';

  if (!name) {
    return <span className={css({ color: theme.colors['text.secondary'] })}>-</span>;
  }

  const delayClose = () => {
    setTimeout(() => setShowPopover(false), 100);
  };

  const nameNode = database ? (
    <span
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
        color: '#156BBF',
        position: 'relative',
        maxWidth: '100%',
      })}
    >
      <EditableText
        ref={handleRef}
        text={database.name}
        compact
        maxWidth="180px"
        disableEdit={!canEdit}
        onClick={() => setShowPopover((prev) => !prev)}
        onTextChange={(nextName) => {
          updateName.mutate(
            {
              group_id: database.workgroup_id,
              db_id: database.database_id,
              database_name: nextName,
            },
            {
              onError(error) {
                showToast({
                  kind: 'negative',
                  message: `${getErrorMessage(error)}`,
                });
                handleRef.current?.reset();
              },
            }
          );
        }}
      />
      <Popover
        isOpen={showPopover}
        onEsc={delayClose}
        onClickOutside={() => {}}
        content={<DatabasePopoverContent database={database} onClose={() => setShowPopover(false)} />}
        placement={PLACEMENT.bottom}
        overrides={popOverrides}
        autoFocus={false}
      >
        <div
          className={css({
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
          })}
        />
      </Popover>
    </span>
  ) : (
    <span>{name}</span>
  );

  return (
    <span className={css({ display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' })}>
      {nameNode}
      {!workspace.is_rw ? <SnapshotTag snapshot={workspace} rwWorkspace={relatedRwWorkspace} /> : null}
    </span>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  const [css, theme] = useStyletron();
  return (
    <div
      className={css({
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        fontSize: '13px',
      })}
    >
      <span
        className={css({
          color: theme.colors['text.secondary'],
        })}
      >
        {label}
      </span>
      <span
        className={css({
          color: theme.colors['text.primary'],
          textAlign: 'right',
        })}
      >
        {value}
      </span>
    </div>
  );
}

/** Full-bleed horizontal rule that cancels the card's 16px horizontal padding. */
function CardDivider() {
  const [css, theme] = useStyletron();
  return (
    <div
      className={css({
        height: '1px',
        backgroundColor: theme.colors.divider,
        marginLeft: '-16px',
        marginRight: '-16px',
      })}
    />
  );
}

function CardFooter({ workspace }: { workspace: WorkspaceT }) {
  const [css] = useStyletron();
  const navigate = useNavigate();
  const [, setOnboardingState] = useAtom(onboardingAtom);

  const enabled = isStatusActive(workspace.status) && workspace.canAccess;

  const firstRowButtonOverrides = {
    BaseButton: {
      style: {
        flex: 1,
        fontSize: '13px',
        ...expand({
          padding: '6px',
        }),
      },
    },
  };

  const secondRowButtonOverrides = {
    BaseButton: {
      style: {
        flex: 1,
        fontSize: '16px',
        height: '44px',
        ...expand({
          padding: '12px 16px',
        }),
      },
    },
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
      })}
    >
      <div
        className={css({
          display: 'flex',
          gap: '8px',
        })}
      >
        <Button
          kind="secondary"
          onClick={() => {
            setOnboardingState({
              ...defaultOnboardingState,
              workspace: workspace,
            });
            navigate(`/onboarding/import-csv?workspace_id=${workspace.workspace_id}&source=card_view`);
          }}
          disabled={!enabled}
          overrides={firstRowButtonOverrides}
        >
          Build AI
        </Button>
        <Button
          kind="secondary"
          onClick={() => navigate('/ingestion?workspace_id=' + workspace.workspace_id)}
          disabled={!enabled}
          overrides={firstRowButtonOverrides}
        >
          Load Data
        </Button>
        <Button
          kind="secondary"
          onClick={() => navigate(`/explore?workspace_id=${encodeURIComponent(workspace.workspace_id)}`)}
          disabled={!enabled}
          overrides={firstRowButtonOverrides}
        >
          Explore Graph
        </Button>
      </div>
      <div
        className={css({
          display: 'flex',
          gap: '8px',
        })}
      >
        <Button
          kind="primary"
          onClick={() => navigate('/design-schema?workspace_id=' + workspace.workspace_id)}
          disabled={!enabled}
          overrides={secondRowButtonOverrides}
        >
          Design Schema
        </Button>
        <Button
          kind="secondary"
          onClick={() => {
            navigate(`/editor?workspace_id=${encodeURIComponent(workspace.workspace_id)}`);
          }}
          disabled={!enabled}
          overrides={secondRowButtonOverrides}
        >
          Query Editor
        </Button>
      </div>
    </div>
  );
}

function AccessBadge({ isRw }: { isRw: boolean }) {
  const [css] = useStyletron();
  return (
    <span
      className={css({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: '40px',
        padding: '2px 8px',
        borderRadius: '4px',
        border: `1px solid ${isRw ? '#BEDBFF' : '#E5E7EB'}`,
        color: isRw ? '#1447E6' : '#364153',
        fontSize: '12px',
        fontWeight: 500,
        backgroundColor: isRw ? '#EFF6FF' : '#F9FAFB',
      })}
    >
      {isRw ? 'R/W' : 'R/O'}
    </span>
  );
}

function StandaloneDatabaseCard({
  database,
  checked,
  onToggle,
}: {
  database: DatabaseT;
  checked: boolean;
  onToggle: () => void;
}) {
  const [css, theme] = useStyletron();
  const { userInfo } = useOrgContext();
  const deleteDatabaseMutation = useMutationDeleteDatabase();
  const updateName = useMutationRenameDatabase();
  const handleRef = useRef<EditableTextHandle | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPopover, setShowPopover] = useState(false);

  const isGroupAdmin = calculateRoleForGroup(userInfo.roles, database.workgroup_id) === 'workgroup-admins';

  const delayClose = () => {
    setTimeout(() => setShowPopover(false), 100);
  };

  const onDelete = () => {
    const promise = deleteDatabaseMutation.mutateAsync({
      group_id: database.workgroup_id,
      db_id: database.database_id,
    });

    toast.promise(promise, {
      loading: 'Deleting database',
      success: (result) => result.Message!,
      error: (err) => `${getErrorMessage(err)}`,
    });
  };

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '8px',
        border: `1px solid ${checked ? theme.colors['border.selected'] : theme.colors.divider}`,
        backgroundColor: theme.colors['background.alternative'],
        overflow: 'hidden',
      })}
    >
      <div
        className={css({
          padding: '8px 16px',
          borderBottom: '1px solid #FFD0A0',
          backgroundColor: '#FFF8F0',
          color: '#B85C00',
          fontWeight: 500,
        })}
      >
        No workspace associated
      </div>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          padding: '16px',
        })}
      >
        <div
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          })}
        >
          <Checkbox
            aria-label={`Select database ${database.name}`}
            checked={checked}
            onChange={onToggle}
            overrides={{
              Root: {
                style: {
                  marginRight: 0,
                },
              },
            }}
          />
          <div
            className={css({
              flex: 1,
              minWidth: 0,
              fontSize: '16px',
              fontWeight: 600,
              color: '#156BBF',
              position: 'relative',
            })}
          >
            <EditableText
              ref={handleRef}
              text={database.name}
              compact
              maxWidth="100%"
              disableEdit={!isGroupAdmin}
              onClick={() => setShowPopover((prev) => !prev)}
              onTextChange={(nextName) => {
                updateName.mutate(
                  {
                    group_id: database.workgroup_id,
                    db_id: database.database_id,
                    database_name: nextName,
                  },
                  {
                    onError(error) {
                      showToast({
                        kind: 'negative',
                        message: `${getErrorMessage(error)}`,
                      });
                      handleRef.current?.reset();
                    },
                  }
                );
              }}
            />
            <Popover
              isOpen={showPopover}
              onEsc={delayClose}
              onClickOutside={() => {}}
              content={<DatabasePopoverContent database={database} onClose={() => setShowPopover(false)} />}
              placement={PLACEMENT.bottom}
              overrides={popOverrides}
              autoFocus={false}
            >
              <div
                className={css({
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  bottom: 0,
                })}
              />
            </Popover>
          </div>
        </div>

        <DetailRow label="ID:" value={<span>{database.database_id}</span>} />
        <DetailRow label="Workgroup ID:" value={<span>{database.workgroup_id}</span>} />
        <DetailRow
          label="Created:"
          value={<span>{format(parseDate(database.create_time), 'yyyy-MM-dd HH:mm:ss')}</span>}
        />

        <CardDivider />

        {isGroupAdmin ? (
          <Button kind="primary" size="large" onClick={() => setShowDeleteConfirm(true)}>
            Terminate
          </Button>
        ) : null}
      </div>

      {showDeleteConfirm ? (
        <ConfirmDeleteDatabase
          isOpen={showDeleteConfirm}
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={() => {
            setShowDeleteConfirm(false);
            onDelete();
          }}
          name={database.name}
        />
      ) : null}
    </div>
  );
}
